/**
 * useAchievements Hook
 *
 * Calculates unlocked achievements and progress based on trading history.
 * Persists unlock events to the backend so timestamps survive across sessions.
 */

import { useMemo, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@nordex/core";
import type { CompletedTrade, Achievement, AdvancedStats } from "@nordex/core";

/**
 * Achievement definitions - 50+ achievements across 6 categories
 */
const ACHIEVEMENT_DEFS: Omit<Achievement, "unlocked" | "progress" | "unlockedAt" | "currentValue">[] = [
  // ============================================
  // MILESTONES - Trade Count Progression
  // ============================================
  {
    id: "first_blood",
    label: "First Blood",
    description: "Complete your first profitable trade",
    icon: "🎯",
    category: "milestone",
    targetValue: 1,
  },
  {
    id: "first_ten",
    label: "Getting Started",
    description: "Complete 10 trades",
    icon: "🌱",
    category: "milestone",
    targetValue: 10,
  },
  {
    id: "half_century",
    label: "Fifty Trades",
    description: "Complete 50 trades",
    icon: "📈",
    category: "milestone",
    targetValue: 50,
  },
  {
    id: "century_club",
    label: "Century Club",
    description: "Complete 100 trades",
    icon: "💯",
    category: "milestone",
    targetValue: 100,
  },
  {
    id: "two_fifty",
    label: "Quarter K",
    description: "Complete 250 trades",
    icon: "🎯",
    category: "milestone",
    targetValue: 250,
  },
  {
    id: "five_hundred",
    label: "Half K",
    description: "Complete 500 trades",
    icon: "⚡",
    category: "milestone",
    targetValue: 500,
  },
  {
    id: "thousand_trades",
    label: "Thousand Trades",
    description: "Complete 1,000 trades",
    icon: "🏆",
    category: "milestone",
    targetValue: 1000,
  },
  {
    id: "twenty_five_hundred",
    label: "Seasoned Trader",
    description: "Complete 2,500 trades",
    icon: "🎖️",
    category: "milestone",
    targetValue: 2500,
  },
  {
    id: "five_k_trades",
    label: "5K Veteran",
    description: "Complete 5,000 trades",
    icon: "🥇",
    category: "milestone",
    targetValue: 5000,
  },
  {
    id: "ten_k_trades",
    label: "10K Legend",
    description: "Complete 10,000 trades",
    icon: "👑",
    category: "milestone",
    targetValue: 10000,
  },
  {
    id: "twenty_five_k_trades",
    label: "Market Machine",
    description: "Complete 25,000 trades",
    icon: "🤖",
    category: "milestone",
    targetValue: 25000,
  },
  {
    id: "fifty_k_trades",
    label: "Trading God",
    description: "Complete 50,000 trades",
    icon: "⚜️",
    category: "milestone",
    targetValue: 50000,
  },

  // Profit Milestones
  {
    id: "first_hundred",
    label: "First $100",
    description: "Earn $100 in total profits",
    icon: "💵",
    category: "milestone",
    targetValue: 100,
  },
  {
    id: "one_k_profit",
    label: "$1K Club",
    description: "Earn $1,000 in total profits",
    icon: "💸",
    category: "milestone",
    targetValue: 1000,
  },
  {
    id: "five_k_profit",
    label: "$5K Club",
    description: "Earn $5,000 in total profits",
    icon: "💰",
    category: "milestone",
    targetValue: 5000,
  },
  {
    id: "ten_k_profit",
    label: "$10K Club",
    description: "Earn $10,000 in total profits",
    icon: "🤑",
    category: "milestone",
    targetValue: 10000,
  },
  {
    id: "twenty_five_k_profit",
    label: "$25K Club",
    description: "Earn $25,000 in total profits",
    icon: "💎",
    category: "milestone",
    targetValue: 25000,
  },
  {
    id: "fifty_k_profit",
    label: "$50K Club",
    description: "Earn $50,000 in total profits",
    icon: "💍",
    category: "milestone",
    targetValue: 50000,
  },
  {
    id: "hundred_k_profit",
    label: "$100K Club",
    description: "Earn $100,000 in total profits",
    icon: "🏦",
    category: "milestone",
    targetValue: 100000,
  },
  {
    id: "quarter_mil_profit",
    label: "$250K Club",
    description: "Earn $250,000 in total profits",
    icon: "🏰",
    category: "milestone",
    targetValue: 250000,
  },
  {
    id: "half_mil_profit",
    label: "$500K Club",
    description: "Earn $500,000 in total profits",
    icon: "🚀",
    category: "milestone",
    targetValue: 500000,
  },
  {
    id: "million_profit",
    label: "Millionaire",
    description: "Earn $1,000,000 in total profits",
    icon: "🌟",
    category: "milestone",
    targetValue: 1000000,
  },

  // Time-based Milestones
  {
    id: "first_week",
    label: "Week One",
    description: "Trade for 7 days",
    icon: "📅",
    category: "milestone",
    targetValue: 7,
  },
  {
    id: "first_month",
    label: "Monthly Trader",
    description: "Trade for 30 days",
    icon: "📆",
    category: "milestone",
    targetValue: 30,
  },
  {
    id: "quarter_year",
    label: "Quarterly Trader",
    description: "Trade for 90 days",
    icon: "🗓️",
    category: "milestone",
    targetValue: 90,
  },
  {
    id: "half_year",
    label: "6 Month Veteran",
    description: "Trade for 180 days",
    icon: "📊",
    category: "milestone",
    targetValue: 180,
  },
  {
    id: "one_year",
    label: "Annual Trader",
    description: "Trade for 365 days",
    icon: "🎂",
    category: "milestone",
    targetValue: 365,
  },

  // ============================================
  // STREAKS
  // ============================================
  {
    id: "hot_streak_3",
    label: "Hot Streak",
    description: "Win 3 trades in a row",
    icon: "🔥",
    category: "streak",
    targetValue: 3,
  },
  {
    id: "hot_streak_5",
    label: "On Fire",
    description: "Win 5 trades in a row",
    icon: "🔥🔥",
    category: "streak",
    targetValue: 5,
  },
  {
    id: "hot_streak_10",
    label: "Unstoppable",
    description: "Win 10 trades in a row",
    icon: "⚡",
    category: "streak",
    targetValue: 10,
  },
  {
    id: "hot_streak_15",
    label: "Legendary Run",
    description: "Win 15 trades in a row",
    icon: "🌪️",
    category: "streak",
    targetValue: 15,
  },
  {
    id: "hot_streak_20",
    label: "Untouchable",
    description: "Win 20 trades in a row",
    icon: "👑",
    category: "streak",
    targetValue: 20,
  },
  {
    id: "green_week",
    label: "Green Week",
    description: "7 consecutive profitable trading days",
    icon: "📈",
    category: "streak",
    targetValue: 7,
  },
  {
    id: "green_two_weeks",
    label: "Green Fortnight",
    description: "14 consecutive profitable trading days",
    icon: "📈📈",
    category: "streak",
    targetValue: 14,
  },
  {
    id: "green_month",
    label: "Green Month",
    description: "30 consecutive profitable trading days",
    icon: "🌲",
    category: "streak",
    targetValue: 30,
  },

  // ============================================
  // PERFORMANCE
  // ============================================
  {
    id: "sharp_shooter",
    label: "Sharpshooter",
    description: "Achieve 70%+ win rate (min 20 trades)",
    icon: "🎯",
    category: "performance",
    targetValue: 70,
  },
  {
    id: "elite_shooter",
    label: "Elite Shooter",
    description: "Achieve 80%+ win rate (min 50 trades)",
    icon: "🎯🎯",
    category: "performance",
    targetValue: 80,
  },
  {
    id: "profit_machine",
    label: "Profit Machine",
    description: "Achieve profit factor above 2.0 (min 20 trades)",
    icon: "⚙️",
    category: "performance",
    targetValue: 2,
  },
  {
    id: "profit_monster",
    label: "Profit Monster",
    description: "Achieve profit factor above 3.0 (min 50 trades)",
    icon: "🦾",
    category: "performance",
    targetValue: 3,
  },
  {
    id: "risk_manager",
    label: "Risk Manager",
    description: "Keep max drawdown below 10% (min 30 days)",
    icon: "🛡️",
    category: "performance",
    targetValue: 10,
  },
  {
    id: "risk_master",
    label: "Risk Master",
    description: "Keep max drawdown below 5% (min 60 days)",
    icon: "🏰",
    category: "performance",
    targetValue: 5,
  },
  {
    id: "high_rr",
    label: "Risk/Reward King",
    description: "Average R:R ratio above 2:1 (min 30 trades)",
    icon: "⚖️",
    category: "performance",
    targetValue: 2,
  },

  // ============================================
  // DIVERSITY
  // ============================================
  {
    id: "diversified",
    label: "Diversified",
    description: "Trade 5+ different assets profitably",
    icon: "🌐",
    category: "diversity",
    targetValue: 5,
  },
  {
    id: "super_diversified",
    label: "Portfolio Pro",
    description: "Trade 10+ different assets profitably",
    icon: "🌍",
    category: "diversity",
    targetValue: 10,
  },
  {
    id: "category_master",
    label: "Category Master",
    description: "Profit in 4+ token categories",
    icon: "🎓",
    category: "diversity",
    targetValue: 4,
  },
  {
    id: "market_explorer",
    label: "Market Explorer",
    description: "Trade 20+ unique markets",
    icon: "🗺️",
    category: "diversity",
    targetValue: 20,
  },

  // ============================================
  // SPECIAL
  // ============================================
  {
    id: "comeback_king",
    label: "Comeback King",
    description: "Recover from 20%+ drawdown to new ATH",
    icon: "👑",
    category: "special",
    targetValue: 1,
  },
  {
    id: "perfect_day",
    label: "Perfect Day",
    description: "100% win rate in a day (min 5 trades)",
    icon: "✨",
    category: "special",
    targetValue: 1,
  },
  {
    id: "perfect_week",
    label: "Perfect Week",
    description: "100% win rate in a week (min 20 trades)",
    icon: "🌟",
    category: "special",
    targetValue: 1,
  },
  {
    id: "big_fish",
    label: "Big Fish",
    description: "Single trade profit exceeds $1,000",
    icon: "🐟",
    category: "special",
    targetValue: 1000,
  },
  {
    id: "whale_catch",
    label: "Whale Catch",
    description: "Single trade profit exceeds $10,000",
    icon: "🐋",
    category: "special",
    targetValue: 10000,
  },
  {
    id: "diamond_hands",
    label: "Diamond Hands",
    description: "Hold a winning position for 7+ days",
    icon: "💎",
    category: "special",
    targetValue: 7,
  },
  {
    id: "night_warrior",
    label: "Night Warrior",
    description: "Win 10+ trades between midnight and 6AM",
    icon: "🌙",
    category: "special",
    targetValue: 10,
  },

  // ============================================
  // LEGENDS - Famous Traders
  // ============================================
  {
    id: "warren_buffett",
    label: "The Oracle",
    description: "Avg hold time >7 days with profit factor >2.0 (50+ trades)",
    icon: "🦅",
    category: "legend",
    targetValue: 1,
  },
  {
    id: "jesse_livermore",
    label: "The Great Bear",
    description: "Earn $50,000+ profit from short positions alone",
    icon: "🐻",
    category: "legend",
    targetValue: 50000,
  },
  {
    id: "paul_tudor_jones",
    label: "The Macro Master",
    description: "Profit factor >2.5 with max drawdown <8% over 90+ days",
    icon: "🌊",
    category: "legend",
    targetValue: 1,
  },
  {
    id: "george_soros",
    label: "The Speculator",
    description: "Single trade profit exceeds $25,000",
    icon: "🦈",
    category: "legend",
    targetValue: 25000,
  },
  {
    id: "ray_dalio",
    label: "All Weather",
    description: "Profitable in both long and short (50+ trades each)",
    icon: "⚖️",
    category: "legend",
    targetValue: 1,
  },
  {
    id: "stanley_druckenmiller",
    label: "The Protégé",
    description: "30+ consecutive profitable trading days",
    icon: "📊",
    category: "legend",
    targetValue: 30,
  },
  {
    id: "jim_simons",
    label: "The Quant",
    description: "Win rate >65% with 1000+ trades and profit factor >1.8",
    icon: "🧮",
    category: "legend",
    targetValue: 1,
  },
  {
    id: "michael_burry",
    label: "The Contrarian",
    description: "Profit $100K+ while being net short",
    icon: "🔮",
    category: "legend",
    targetValue: 100000,
  },
  {
    id: "ed_seykota",
    label: "The Trend Follower",
    description: "Win 20+ trades in a row",
    icon: "〰️",
    category: "legend",
    targetValue: 20,
  },
  {
    id: "nicolas_darvas",
    label: "The Box Trader",
    description: "5 consecutive trades with R:R above 3:1",
    icon: "📦",
    category: "legend",
    targetValue: 5,
  },
];

/**
 * Calculate achievements from trading data
 */
function calculateAchievements(
  trades: CompletedTrade[],
  stats: AdvancedStats
): Achievement[] {
  // Sort trades chronologically
  const sortedTrades = [...trades].sort(
    (a, b) => a.exitTime.getTime() - b.exitTime.getTime()
  );

  // Track profitable assets and categories
  const profitByMarket = new Map<string, number>();
  const profitByCategory = new Map<string, number>();
  const uniqueMarkets = new Set<string>();

  for (const trade of trades) {
    uniqueMarkets.add(trade.market);
    const marketProfit = (profitByMarket.get(trade.market) || 0) + trade.netPnl;
    profitByMarket.set(trade.market, marketProfit);

    const catProfit =
      (profitByCategory.get(trade.category) || 0) + trade.netPnl;
    profitByCategory.set(trade.category, catProfit);
  }

  const profitableMarkets = Array.from(profitByMarket.entries()).filter(
    ([_, pnl]) => pnl > 0
  ).length;
  const profitableCategories = Array.from(profitByCategory.entries()).filter(
    ([_, pnl]) => pnl > 0
  ).length;

  // Track consecutive profitable days
  const dailyPnl = new Map<string, number>();
  for (const trade of sortedTrades) {
    const day = trade.exitTime.toISOString().split("T")[0];
    dailyPnl.set(day, (dailyPnl.get(day) || 0) + trade.netPnl);
  }

  const sortedDays = Array.from(dailyPnl.entries()).sort(([a], [b]) =>
    a.localeCompare(b)
  );

  let maxConsecutiveGreenDays = 0;
  let currentGreenDays = 0;

  for (const [_, pnl] of sortedDays) {
    if (pnl > 0) {
      currentGreenDays++;
      maxConsecutiveGreenDays = Math.max(
        maxConsecutiveGreenDays,
        currentGreenDays
      );
    } else {
      currentGreenDays = 0;
    }
  }

  // Check for perfect days (100% win rate, min 5 trades)
  const tradesByDay = new Map<string, CompletedTrade[]>();
  for (const trade of sortedTrades) {
    const day = trade.exitTime.toISOString().split("T")[0];
    const existing = tradesByDay.get(day) || [];
    existing.push(trade);
    tradesByDay.set(day, existing);
  }

  let hasPerfectDay = false;
  for (const [_, dayTrades] of tradesByDay) {
    if (dayTrades.length >= 5) {
      const allWins = dayTrades.every((t) => t.netPnl > 0);
      if (allWins) {
        hasPerfectDay = true;
        break;
      }
    }
  }

  // Check for perfect week (100% win rate, min 20 trades in a week)
  let hasPerfectWeek = false;
  const weeklyTrades = new Map<string, CompletedTrade[]>();
  for (const trade of sortedTrades) {
    const date = new Date(trade.exitTime);
    const weekStart = new Date(date);
    weekStart.setDate(date.getDate() - date.getDay());
    const weekKey = weekStart.toISOString().split("T")[0];
    const existing = weeklyTrades.get(weekKey) || [];
    existing.push(trade);
    weeklyTrades.set(weekKey, existing);
  }
  for (const [_, weekTrades] of weeklyTrades) {
    if (weekTrades.length >= 20) {
      const allWins = weekTrades.every((t) => t.netPnl > 0);
      if (allWins) {
        hasPerfectWeek = true;
        break;
      }
    }
  }

  // Check for comeback (recovered from 20%+ drawdown)
  let hadLargeDrawdown = false;
  let recoveredFromDrawdown = false;
  let runningPnl = 0;
  let peak = 0;

  for (const trade of sortedTrades) {
    runningPnl += trade.netPnl;
    if (runningPnl > peak) {
      if (hadLargeDrawdown) {
        recoveredFromDrawdown = true;
      }
      peak = runningPnl;
    }
    const drawdownPercent = peak > 0 ? ((peak - runningPnl) / peak) * 100 : 0;
    if (drawdownPercent >= 20) {
      hadLargeDrawdown = true;
    }
  }

  // Find largest single trade profit
  let largestTradeProfit = 0;
  for (const trade of trades) {
    if (trade.netPnl > largestTradeProfit) {
      largestTradeProfit = trade.netPnl;
    }
  }

  // Check for long-held winning positions (7+ days)
  let hasLongHeldWinner = false;
  let longestWinningHoldDays = 0;
  for (const trade of trades) {
    if (trade.netPnl > 0) {
      const holdMs = trade.exitTime.getTime() - trade.entryTime.getTime();
      const holdDays = holdMs / (1000 * 60 * 60 * 24);
      if (holdDays > longestWinningHoldDays) {
        longestWinningHoldDays = holdDays;
      }
      if (holdDays >= 7) {
        hasLongHeldWinner = true;
      }
    }
  }

  // Count night trades (midnight to 6AM UTC)
  let nightWins = 0;
  for (const trade of trades) {
    const hour = trade.exitTime.getUTCHours();
    if (hour >= 0 && hour < 6 && trade.netPnl > 0) {
      nightWins++;
    }
  }

  // Calculate short position profit
  let shortProfit = 0;
  let shortTrades = 0;
  let longProfit = 0;
  let longTrades = 0;
  for (const trade of trades) {
    if (trade.side === "short") {
      shortProfit += trade.netPnl;
      shortTrades++;
    } else {
      longProfit += trade.netPnl;
      longTrades++;
    }
  }

  // Calculate average hold time in days
  const avgHoldDays = stats.avgTradeDurationMs / (1000 * 60 * 60 * 24);

  // Check for consecutive high R:R trades
  let maxConsecutiveHighRR = 0;
  let currentConsecutiveHighRR = 0;
  for (const trade of sortedTrades) {
    // Calculate R:R for this trade (win / potential loss based on avg loss)
    if (trade.netPnl > 0 && stats.averageLoss !== 0) {
      const rr = trade.netPnl / Math.abs(stats.averageLoss);
      if (rr >= 3) {
        currentConsecutiveHighRR++;
        maxConsecutiveHighRR = Math.max(maxConsecutiveHighRR, currentConsecutiveHighRR);
      } else {
        currentConsecutiveHighRR = 0;
      }
    } else {
      currentConsecutiveHighRR = 0;
    }
  }

  // Map achievements
  return ACHIEVEMENT_DEFS.map((def) => {
    let unlocked = false;
    let progress = 0;
    let currentValue: number | undefined;

    switch (def.id) {
      // Trade count milestones
      case "first_blood":
        currentValue = stats.winningTrades;
        unlocked = stats.winningTrades >= 1;
        progress = unlocked ? 100 : 0;
        break;

      case "first_ten":
        currentValue = stats.totalTrades;
        progress = Math.min((stats.totalTrades / 10) * 100, 100);
        unlocked = stats.totalTrades >= 10;
        break;

      case "half_century":
        currentValue = stats.totalTrades;
        progress = Math.min((stats.totalTrades / 50) * 100, 100);
        unlocked = stats.totalTrades >= 50;
        break;

      case "century_club":
        currentValue = stats.totalTrades;
        progress = Math.min((stats.totalTrades / 100) * 100, 100);
        unlocked = stats.totalTrades >= 100;
        break;

      case "two_fifty":
        currentValue = stats.totalTrades;
        progress = Math.min((stats.totalTrades / 250) * 100, 100);
        unlocked = stats.totalTrades >= 250;
        break;

      case "five_hundred":
        currentValue = stats.totalTrades;
        progress = Math.min((stats.totalTrades / 500) * 100, 100);
        unlocked = stats.totalTrades >= 500;
        break;

      case "thousand_trades":
        currentValue = stats.totalTrades;
        progress = Math.min((stats.totalTrades / 1000) * 100, 100);
        unlocked = stats.totalTrades >= 1000;
        break;

      case "twenty_five_hundred":
        currentValue = stats.totalTrades;
        progress = Math.min((stats.totalTrades / 2500) * 100, 100);
        unlocked = stats.totalTrades >= 2500;
        break;

      case "five_k_trades":
        currentValue = stats.totalTrades;
        progress = Math.min((stats.totalTrades / 5000) * 100, 100);
        unlocked = stats.totalTrades >= 5000;
        break;

      case "ten_k_trades":
        currentValue = stats.totalTrades;
        progress = Math.min((stats.totalTrades / 10000) * 100, 100);
        unlocked = stats.totalTrades >= 10000;
        break;

      case "twenty_five_k_trades":
        currentValue = stats.totalTrades;
        progress = Math.min((stats.totalTrades / 25000) * 100, 100);
        unlocked = stats.totalTrades >= 25000;
        break;

      case "fifty_k_trades":
        currentValue = stats.totalTrades;
        progress = Math.min((stats.totalTrades / 50000) * 100, 100);
        unlocked = stats.totalTrades >= 50000;
        break;

      // Profit milestones
      case "first_hundred":
        currentValue = Math.max(0, stats.totalNetPnl);
        progress = Math.min((Math.max(0, stats.totalNetPnl) / 100) * 100, 100);
        unlocked = stats.totalNetPnl >= 100;
        break;

      case "one_k_profit":
        currentValue = Math.max(0, stats.totalNetPnl);
        progress = Math.min((Math.max(0, stats.totalNetPnl) / 1000) * 100, 100);
        unlocked = stats.totalNetPnl >= 1000;
        break;

      case "five_k_profit":
        currentValue = Math.max(0, stats.totalNetPnl);
        progress = Math.min((Math.max(0, stats.totalNetPnl) / 5000) * 100, 100);
        unlocked = stats.totalNetPnl >= 5000;
        break;

      case "ten_k_profit":
        currentValue = Math.max(0, stats.totalNetPnl);
        progress = Math.min((Math.max(0, stats.totalNetPnl) / 10000) * 100, 100);
        unlocked = stats.totalNetPnl >= 10000;
        break;

      case "twenty_five_k_profit":
        currentValue = Math.max(0, stats.totalNetPnl);
        progress = Math.min((Math.max(0, stats.totalNetPnl) / 25000) * 100, 100);
        unlocked = stats.totalNetPnl >= 25000;
        break;

      case "fifty_k_profit":
        currentValue = Math.max(0, stats.totalNetPnl);
        progress = Math.min((Math.max(0, stats.totalNetPnl) / 50000) * 100, 100);
        unlocked = stats.totalNetPnl >= 50000;
        break;

      case "hundred_k_profit":
        currentValue = Math.max(0, stats.totalNetPnl);
        progress = Math.min((Math.max(0, stats.totalNetPnl) / 100000) * 100, 100);
        unlocked = stats.totalNetPnl >= 100000;
        break;

      case "quarter_mil_profit":
        currentValue = Math.max(0, stats.totalNetPnl);
        progress = Math.min((Math.max(0, stats.totalNetPnl) / 250000) * 100, 100);
        unlocked = stats.totalNetPnl >= 250000;
        break;

      case "half_mil_profit":
        currentValue = Math.max(0, stats.totalNetPnl);
        progress = Math.min((Math.max(0, stats.totalNetPnl) / 500000) * 100, 100);
        unlocked = stats.totalNetPnl >= 500000;
        break;

      case "million_profit":
        currentValue = Math.max(0, stats.totalNetPnl);
        progress = Math.min((Math.max(0, stats.totalNetPnl) / 1000000) * 100, 100);
        unlocked = stats.totalNetPnl >= 1000000;
        break;

      // Time-based milestones
      case "first_week":
        currentValue = stats.tradingDays;
        progress = Math.min((stats.tradingDays / 7) * 100, 100);
        unlocked = stats.tradingDays >= 7;
        break;

      case "first_month":
        currentValue = stats.tradingDays;
        progress = Math.min((stats.tradingDays / 30) * 100, 100);
        unlocked = stats.tradingDays >= 30;
        break;

      case "quarter_year":
        currentValue = stats.tradingDays;
        progress = Math.min((stats.tradingDays / 90) * 100, 100);
        unlocked = stats.tradingDays >= 90;
        break;

      case "half_year":
        currentValue = stats.tradingDays;
        progress = Math.min((stats.tradingDays / 180) * 100, 100);
        unlocked = stats.tradingDays >= 180;
        break;

      case "one_year":
        currentValue = stats.tradingDays;
        progress = Math.min((stats.tradingDays / 365) * 100, 100);
        unlocked = stats.tradingDays >= 365;
        break;

      // Streaks
      case "hot_streak_3":
        currentValue = stats.longestWinStreak;
        progress = Math.min((stats.longestWinStreak / 3) * 100, 100);
        unlocked = stats.longestWinStreak >= 3;
        break;

      case "hot_streak_5":
        currentValue = stats.longestWinStreak;
        progress = Math.min((stats.longestWinStreak / 5) * 100, 100);
        unlocked = stats.longestWinStreak >= 5;
        break;

      case "hot_streak_10":
        currentValue = stats.longestWinStreak;
        progress = Math.min((stats.longestWinStreak / 10) * 100, 100);
        unlocked = stats.longestWinStreak >= 10;
        break;

      case "hot_streak_15":
        currentValue = stats.longestWinStreak;
        progress = Math.min((stats.longestWinStreak / 15) * 100, 100);
        unlocked = stats.longestWinStreak >= 15;
        break;

      case "hot_streak_20":
        currentValue = stats.longestWinStreak;
        progress = Math.min((stats.longestWinStreak / 20) * 100, 100);
        unlocked = stats.longestWinStreak >= 20;
        break;

      case "green_week":
        currentValue = maxConsecutiveGreenDays;
        progress = Math.min((maxConsecutiveGreenDays / 7) * 100, 100);
        unlocked = maxConsecutiveGreenDays >= 7;
        break;

      case "green_two_weeks":
        currentValue = maxConsecutiveGreenDays;
        progress = Math.min((maxConsecutiveGreenDays / 14) * 100, 100);
        unlocked = maxConsecutiveGreenDays >= 14;
        break;

      case "green_month":
        currentValue = maxConsecutiveGreenDays;
        progress = Math.min((maxConsecutiveGreenDays / 30) * 100, 100);
        unlocked = maxConsecutiveGreenDays >= 30;
        break;

      // Performance
      case "sharp_shooter":
        currentValue = stats.winRate;
        progress =
          stats.totalTrades >= 20 ? Math.min((stats.winRate / 70) * 100, 100) : 0;
        unlocked = stats.totalTrades >= 20 && stats.winRate >= 70;
        break;

      case "elite_shooter":
        currentValue = stats.winRate;
        progress =
          stats.totalTrades >= 50 ? Math.min((stats.winRate / 80) * 100, 100) : 0;
        unlocked = stats.totalTrades >= 50 && stats.winRate >= 80;
        break;

      case "profit_machine":
        currentValue =
          stats.profitFactor === Infinity ? 999 : stats.profitFactor;
        progress =
          stats.totalTrades >= 20
            ? Math.min((stats.profitFactor / 2) * 100, 100)
            : 0;
        unlocked =
          stats.totalTrades >= 20 &&
          (stats.profitFactor >= 2 || stats.profitFactor === Infinity);
        break;

      case "profit_monster":
        currentValue =
          stats.profitFactor === Infinity ? 999 : stats.profitFactor;
        progress =
          stats.totalTrades >= 50
            ? Math.min((stats.profitFactor / 3) * 100, 100)
            : 0;
        unlocked =
          stats.totalTrades >= 50 &&
          (stats.profitFactor >= 3 || stats.profitFactor === Infinity);
        break;

      case "risk_manager":
        currentValue = 100 - stats.maxDrawdownPercent;
        progress =
          stats.tradingDays >= 30
            ? Math.min(((10 - stats.maxDrawdownPercent) / 10) * 100 + 50, 100)
            : 0;
        unlocked =
          stats.tradingDays >= 30 && stats.maxDrawdownPercent < 10;
        break;

      case "risk_master":
        currentValue = 100 - stats.maxDrawdownPercent;
        progress =
          stats.tradingDays >= 60
            ? Math.min(((5 - stats.maxDrawdownPercent) / 5) * 100 + 50, 100)
            : 0;
        unlocked =
          stats.tradingDays >= 60 && stats.maxDrawdownPercent < 5;
        break;

      case "high_rr":
        currentValue = stats.riskRewardRatio === Infinity ? 999 : stats.riskRewardRatio;
        progress =
          stats.totalTrades >= 30
            ? Math.min((stats.riskRewardRatio / 2) * 100, 100)
            : 0;
        unlocked =
          stats.totalTrades >= 30 &&
          (stats.riskRewardRatio >= 2 || stats.riskRewardRatio === Infinity);
        break;

      // Diversity
      case "diversified":
        currentValue = profitableMarkets;
        progress = Math.min((profitableMarkets / 5) * 100, 100);
        unlocked = profitableMarkets >= 5;
        break;

      case "super_diversified":
        currentValue = profitableMarkets;
        progress = Math.min((profitableMarkets / 10) * 100, 100);
        unlocked = profitableMarkets >= 10;
        break;

      case "category_master":
        currentValue = profitableCategories;
        progress = Math.min((profitableCategories / 4) * 100, 100);
        unlocked = profitableCategories >= 4;
        break;

      case "market_explorer":
        currentValue = uniqueMarkets.size;
        progress = Math.min((uniqueMarkets.size / 20) * 100, 100);
        unlocked = uniqueMarkets.size >= 20;
        break;

      // Special
      case "comeback_king":
        currentValue = recoveredFromDrawdown ? 1 : 0;
        progress = recoveredFromDrawdown ? 100 : hadLargeDrawdown ? 50 : 0;
        unlocked = recoveredFromDrawdown;
        break;

      case "perfect_day":
        currentValue = hasPerfectDay ? 1 : 0;
        progress = hasPerfectDay ? 100 : 0;
        unlocked = hasPerfectDay;
        break;

      case "perfect_week":
        currentValue = hasPerfectWeek ? 1 : 0;
        progress = hasPerfectWeek ? 100 : 0;
        unlocked = hasPerfectWeek;
        break;

      case "big_fish":
        currentValue = largestTradeProfit;
        progress = Math.min((largestTradeProfit / 1000) * 100, 100);
        unlocked = largestTradeProfit >= 1000;
        break;

      case "whale_catch":
        currentValue = largestTradeProfit;
        progress = Math.min((largestTradeProfit / 10000) * 100, 100);
        unlocked = largestTradeProfit >= 10000;
        break;

      case "diamond_hands":
        currentValue = Math.floor(longestWinningHoldDays);
        progress = Math.min((longestWinningHoldDays / 7) * 100, 100);
        unlocked = hasLongHeldWinner;
        break;

      case "night_warrior":
        currentValue = nightWins;
        progress = Math.min((nightWins / 10) * 100, 100);
        unlocked = nightWins >= 10;
        break;

      // Legends
      case "warren_buffett":
        // Avg hold time >7 days with profit factor >2.0 (50+ trades)
        const buffettCheck = avgHoldDays >= 7 && stats.profitFactor >= 2 && stats.totalTrades >= 50;
        currentValue = buffettCheck ? 1 : 0;
        progress = buffettCheck ? 100 : 
          (stats.totalTrades >= 50 ? (avgHoldDays >= 7 ? 66 : 33) : 0);
        unlocked = buffettCheck;
        break;

      case "jesse_livermore":
        // Earn $50,000+ profit from short positions
        currentValue = Math.max(0, shortProfit);
        progress = Math.min((Math.max(0, shortProfit) / 50000) * 100, 100);
        unlocked = shortProfit >= 50000;
        break;

      case "paul_tudor_jones":
        // Profit factor >2.5 with max drawdown <8% over 90+ days
        const ptjCheck = stats.profitFactor >= 2.5 && stats.maxDrawdownPercent < 8 && stats.tradingDays >= 90;
        currentValue = ptjCheck ? 1 : 0;
        progress = ptjCheck ? 100 :
          (stats.tradingDays >= 90 ? (stats.profitFactor >= 2.5 ? 66 : 33) : 0);
        unlocked = ptjCheck;
        break;

      case "george_soros":
        // Single trade profit exceeds $25,000
        currentValue = largestTradeProfit;
        progress = Math.min((largestTradeProfit / 25000) * 100, 100);
        unlocked = largestTradeProfit >= 25000;
        break;

      case "ray_dalio":
        // Profitable in both long and short (50+ trades each)
        const dalioCheck = longProfit > 0 && shortProfit > 0 && longTrades >= 50 && shortTrades >= 50;
        currentValue = dalioCheck ? 1 : 0;
        progress = dalioCheck ? 100 :
          ((longProfit > 0 ? 25 : 0) + (shortProfit > 0 ? 25 : 0) + 
           (longTrades >= 50 ? 25 : (longTrades / 50) * 25) +
           (shortTrades >= 50 ? 25 : (shortTrades / 50) * 25));
        unlocked = dalioCheck;
        break;

      case "stanley_druckenmiller":
        // 30+ consecutive profitable trading days
        currentValue = maxConsecutiveGreenDays;
        progress = Math.min((maxConsecutiveGreenDays / 30) * 100, 100);
        unlocked = maxConsecutiveGreenDays >= 30;
        break;

      case "jim_simons":
        // Win rate >65% with 1000+ trades and profit factor >1.8
        const simonsCheck = stats.winRate >= 65 && stats.totalTrades >= 1000 && stats.profitFactor >= 1.8;
        currentValue = simonsCheck ? 1 : 0;
        progress = simonsCheck ? 100 :
          ((stats.totalTrades >= 1000 ? 33 : (stats.totalTrades / 1000) * 33) +
           (stats.winRate >= 65 ? 33 : (stats.winRate / 65) * 33) +
           (stats.profitFactor >= 1.8 ? 34 : (stats.profitFactor / 1.8) * 34));
        unlocked = simonsCheck;
        break;

      case "michael_burry":
        // Profit $100K+ while being net short
        const burryCheck = shortProfit >= 100000 && shortTrades > longTrades;
        currentValue = Math.max(0, shortProfit);
        progress = shortTrades > longTrades 
          ? Math.min((shortProfit / 100000) * 100, 100)
          : 0;
        unlocked = burryCheck;
        break;

      case "ed_seykota":
        // Win 20+ trades in a row
        currentValue = stats.longestWinStreak;
        progress = Math.min((stats.longestWinStreak / 20) * 100, 100);
        unlocked = stats.longestWinStreak >= 20;
        break;

      case "nicolas_darvas":
        // 5 consecutive trades with R:R above 3:1
        currentValue = maxConsecutiveHighRR;
        progress = Math.min((maxConsecutiveHighRR / 5) * 100, 100);
        unlocked = maxConsecutiveHighRR >= 5;
        break;
    }

    return {
      ...def,
      unlocked,
      progress: Math.round(progress),
      currentValue,
      unlockedAt: undefined,
    };
  });
}

interface PersistedAchievement {
  achievementId: string;
  unlockedAt: string;
  currentValue: number;
}

interface PersistedAchievementsResponse {
  achievements: PersistedAchievement[];
  count: number;
}

/**
 * Hook for achievements with backend persistence.
 * Computes achievements client-side, persists unlocks to the backend,
 * and merges stored timestamps into the computed results.
 */
export function useAchievements(
  trades: CompletedTrade[],
  stats: AdvancedStats
): {
  achievements: Achievement[];
  unlockedCount: number;
  totalCount: number;
} {
  const computed = useMemo(
    () => calculateAchievements(trades, stats),
    [trades, stats]
  );

  const { data: persisted } = useQuery<PersistedAchievementsResponse>({
    queryKey: ["nordex-achievements-persisted"],
    queryFn: () => apiClient.get<PersistedAchievementsResponse>("/api/achievements/me"),
    staleTime: 60_000,
    retry: 1,
  });

  const persistedMap = useMemo(() => {
    const map = new Map<string, string>();
    if (persisted?.achievements) {
      for (const a of persisted.achievements) {
        map.set(a.achievementId, a.unlockedAt);
      }
    }
    return map;
  }, [persisted]);

  const achievements = useMemo(() => {
    return computed.map((a) => ({
      ...a,
      unlockedAt: a.unlocked
        ? persistedMap.get(a.id)
          ? new Date(persistedMap.get(a.id)!)
          : undefined
        : undefined,
    }));
  }, [computed, persistedMap]);

  const newlyUnlocked = useMemo(() => {
    return computed.filter(
      (a) => a.unlocked && !persistedMap.has(a.id)
    );
  }, [computed, persistedMap]);

  const syncedIdsRef = useRef(new Set<string>());
  useEffect(() => {
    const toSync = newlyUnlocked.filter((a) => !syncedIdsRef.current.has(a.id));
    if (toSync.length === 0) return;

    for (const a of toSync) syncedIdsRef.current.add(a.id);

    apiClient
      .post("/api/achievements/sync", {
        achievements: toSync.map((a) => ({
          achievementId: a.id,
          currentValue: a.currentValue ?? 0,
        })),
      })
      .catch(() => {
        for (const a of toSync) syncedIdsRef.current.delete(a.id);
      });
  }, [newlyUnlocked]);

  const unlockedCount = achievements.filter((a) => a.unlocked).length;
  const totalCount = achievements.length;

  return {
    achievements,
    unlockedCount,
    totalCount,
  };
}

/**
 * Get achievements by category
 */
export function groupAchievementsByCategory(
  achievements: Achievement[]
): Map<Achievement["category"], Achievement[]> {
  const grouped = new Map<Achievement["category"], Achievement[]>();

  for (const achievement of achievements) {
    const existing = grouped.get(achievement.category) || [];
    existing.push(achievement);
    grouped.set(achievement.category, existing);
  }

  return grouped;
}
